export type OperationStatus = "success" | "error" | "pending";

interface OperationStatusCandidate {
  status?: string;
  state?: string;
  operation_type?: string;
  message?: string;
  detail?: string;
  error?: {
    title?: string;
  };
}

export const OPERATION_WARNING_STATES = [
  "IDLE",
  "IN_PROGRESS",
  "PARTIAL_SUCCESS",
];

const OPERATION_ERROR_STATES = [
  "ERROR",
  "FAILED",
  "FAILURE",
  "BACKEND_FAILURE",
  "TIMED_OUT",
] as const;

const SUCCESS_STATES = new Set(["success"]);
const ERROR_STATES = new Set(
  OPERATION_ERROR_STATES.map((state) => state.toLowerCase()),
);

const readNormalizedStates = (operation?: OperationStatusCandidate): string[] =>
  [operation?.state, operation?.status]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase().replace(/-/g, "_"));

export const getOperationState = (
  operation?: OperationStatusCandidate,
): string =>
  (operation?.state || operation?.status || "")
    .toUpperCase()
    .replace(/-/g, "_");

export const resolveOperationStatus = (
  operation?: OperationStatusCandidate,
): OperationStatus => {
  const normalizedStates = readNormalizedStates(operation);

  if (normalizedStates.some((state) => ERROR_STATES.has(state))) {
    return "error";
  }

  if (normalizedStates.some((state) => SUCCESS_STATES.has(state))) {
    return "success";
  }

  return "pending";
};

export const resolveOperationFailureReason = (
  operation?: OperationStatusCandidate,
): string | undefined => {
  const normalizedStatus = getOperationState(operation);

  if (!ERROR_STATES.has(normalizedStatus.toLowerCase())) {
    return undefined;
  }

  if (operation?.detail?.trim()) {
    return operation.detail.trim();
  }

  if (operation?.message?.trim()) {
    return operation.message.trim();
  }

  if (operation?.error?.title?.trim()) {
    return operation.error.title.trim();
  }

  if (normalizedStatus === "TIMED_OUT") {
    return "The operation timed out.";
  }

  if (normalizedStatus === "BACKEND_FAILURE") {
    return "The backend reported a failure.";
  }

  if (normalizedStatus === "ERROR") {
    return "The operation returned ERROR state.";
  }

  return "The operation failed.";
};
